

import React, { useEffect, useCallback, useState, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import HeroSection from '@/components/HeroSection';
import Footer from '@/components/Footer';
import MetaTags from '@/components/SEO/MetaTags';

// Lazy load below-the-fold sections - hero is the only above-the-fold content
const ProductGallery = lazy(() => import('@/components/ProductGallery'));
const WhyItWorksSection = lazy(() => import('@/components/WhyItWorksSection'));

// Lazy load below-the-fold components for code splitting
const Newsletter = lazy(() => import('@/components/Newsletter'));
const ReviewsPreviewSection = lazy(() => import('@/components/sections/ReviewsPreviewSection'));
const CheckoutCTASection = lazy(() => import('@/components/sections/CheckoutCTASection'));
const TaggboxFeed = lazy(() => import('@/components/sections/TaggboxFeed'));

const PressSection = lazy(() => import('@/components/sections/PressSection'));
const BrandTimelineSection = lazy(() => import('@/components/sections/BrandTimelineSection'));
const KeyBenefitsSection = lazy(() => import('@/components/sections/KeyBenefitsSection'));

// Simple loading fallback component
const SectionLoader = () => (
  <div className="w-full py-8 flex justify-center items-center">
    <div className="w-8 h-8 border-3 border-gray-200 border-t-gray-600 rounded-full animate-spin"></div>
  </div>
);


const Index = () => {
  const { t } = useTranslation();
  const location = useLocation();

  // Removed redundant image prefetch — secondary images are below the fold
  // and competing with the LCP image for bandwidth. They will be fetched
  // lazily by their own <img> tags when needed.

  // Optimized scroll handler with useCallback
  const handleSectionScroll = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navbarHeight;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (location.state?.scrollTo) {
      const sectionId = location.state.scrollTo;
      requestAnimationFrame(() => {
        setTimeout(() => handleSectionScroll(sectionId), 300);
      });
      window.history.replaceState({}, document.title);
    }
  }, [location.state, handleSectionScroll]);

  useEffect(() => {
    const trackPageView = () => {
      if (window.gtag) {
        window.gtag('event', 'page_view', {
          page_title: 'SUMSAE K-Beauty Homepage - Korean Skincare Products & Solutions',
          page_location: window.location.href,
          page_path: location.pathname,
          event_category: 'Page View',
          event_label: 'Homepage - Main Landing Page with Products, Reviews & Why It Works',
          content_group1: 'Homepage',
          content_group2: 'Landing Page',
          send_to: 'G-GT45QQEG2R'
        });
      }
    };

    // Defer analytics tracking to not block rendering
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(trackPageView, { timeout: 3000 });
    } else {
      setTimeout(trackPageView, 2000); // Increased delay for better initial load
    }
  }, [location]);



  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <MetaTags 
        title="SUMSAE | Korean Premium Skincare"
        description="Shop SUMSAE's Korean skincare essentials, including our Patented Waterless Pure Vitamin C Serum. Gentle, effective K-beauty formulas for sensitive, glowing skin."
        keywords="Korean skincare, K-beauty, vitamin C serum, sensitive skin, korean beauty products, SUMSAE, vitamin C for sensitive skin, korean skincare routine"
        canonical="https://sumsae.shop/"
      />
      <Navbar />
      <main className="w-full">
        {/* Critical above-the-fold content - loaded immediately */}
        <div id="hero-section">
          <HeroSection />
        </div>
        
        <Suspense fallback={null}>
          <PressSection />
        </Suspense>

        <Suspense fallback={<SectionLoader />}>
          <KeyBenefitsSection />
        </Suspense>

        <Suspense fallback={<SectionLoader />}>
          <div id="products">
            <ProductGallery />
          </div>
        </Suspense>

        <Suspense fallback={<SectionLoader />}>
          <BrandTimelineSection />
        </Suspense>

        <Suspense fallback={<SectionLoader />}>
          <div id="why-it-works">
            <WhyItWorksSection />
          </div>
        </Suspense>
        
        {/* Below-the-fold content - lazy loaded with Suspense */}
        <Suspense fallback={<SectionLoader />}>
          <div id="instagram" className="bg-kbeauty-cream">
            <TaggboxFeed />
          </div>
        </Suspense>
        
        <Suspense fallback={<SectionLoader />}>
          <div id="reviews">
            <ReviewsPreviewSection />
          </div>
        </Suspense>
        
        <Suspense fallback={<SectionLoader />}>
          <CheckoutCTASection />
        </Suspense>
        
        <Suspense fallback={<SectionLoader />}>
          <Newsletter />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
};

export default Index;
